import { useId, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { OverlayPanel } from 'primereact/overlaypanel';
import { DateTimeInput } from '../DateTimeInput/DateTimeInput';
import {
    dateTimeFilterToBounds,
    describeDateTimeFilter,
    getFilterNow,
    parseFilterDate,
    serializeFilterDate,
    type DateTimeFilterMode,
} from '@/shared/lib/dateTimeFilter';
import classes from './DateTimeFilter.module.scss';

interface DateTimeFilterProps {
    label: string;
    value: unknown;

    /** Применяет или снимает условие календаря */
    onChange: (value: string[] | null) => void;
}

const MODES = [
    {
        label: 'За день',
        value: 'day'
    },
    {
        label: 'До даты и времени',
        value: 'before'
    },
    {
        label: 'Начиная с даты и времени',
        value: 'after'
    },
    {
        label: 'Период',
        value: 'range'
    },
];

/** Выбирает условие даты и времени для колонки таблицы */
export const DateTimeFilter = ({ label, value, onChange }: DateTimeFilterProps) => {
    const panel = useRef<OverlayPanel>(null);
    const trigger = useRef<HTMLButtonElement | null>(null);
    const id = useId();
    const [mode, setMode] = useState<DateTimeFilterMode>('day');
    const [start, setStart] = useState<Date | null>(null);
    const [end, setEnd] = useState<Date | null>(null);
    const [error, setError] = useState('');
    const active = Boolean(dateTimeFilterToBounds(value));

    const reversedRange = mode === 'range' && start !== null && end !== null
        && start.getTime() > end.getTime();

    const validationError = reversedRange
        ? 'Начало периода не должно быть позже конца'
        : error;

    /** Открывает календарь с применённым условием */
    const open = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        trigger.current = event.currentTarget;

        const saved = Array.isArray(value) && dateTimeFilterToBounds(value)
            ? value
            : ['day', '', ''];

        setMode(saved[0] as DateTimeFilterMode);
        setStart(parseFilterDate(saved[1]));
        setEnd(parseFilterDate(saved[2]));
        setError('');
        panel.current?.toggle(event);
    };

    /** Закрывает календарь и возвращает фокус */
    const close = () => {
        panel.current?.hide();
        trigger.current?.focus();
    };

    /** Применяет выбранные границы */
    const apply = () => {
        const next = mode === 'range'
            ? [mode, serializeFilterDate(start), serializeFilterDate(end)]
            : [mode, serializeFilterDate(start)];

        if (!dateTimeFilterToBounds(next)) {
            setError(mode === 'range'
                ? 'Выберите начало и конец периода: начало не должно быть позже конца'
                : 'Выберите дату');

            return;
        }

        onChange(next);
        close();
    };

    return <span className={classes.header}>
        <span>{label}</span>
        <Button
            type="button"
            icon={active
                ? 'pi pi-filter-fill'
                : 'pi pi-filter'
            }
            text
            size="small"
            aria-label={`Фильтр: ${label}`}
            aria-haspopup="dialog"
            title={active
                ? `${describeDateTimeFilter(value)} (МСК)`
                : 'Выбрать дату и время (МСК)'
            }
            onClick={open}
            onKeyDown={(event) => event.stopPropagation()}
        />
        <OverlayPanel ref={panel}>
            <div
                className={classes.panel}
                role="dialog"
                aria-label={`Фильтр: ${label}`}
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => { if (event.key !== 'Escape') event.stopPropagation(); }}
            >
                <strong>{label} · МСК</strong>
                <label htmlFor={`${id}-mode`}>Условие</label>
                <Dropdown
                    inputId={`${id}-mode`}
                    value={mode}
                    options={MODES}
                    pt={{
                        trigger: { 'aria-label': 'Открыть выбор условия' },
                        select: { 'aria-label': 'Условие календарного фильтра' },
                    }}
                    onChange={(event) => {
                        setMode(event.value);
                        setError('');
                    }}
                />
                <DateTimeInput
                    label={mode === 'range'
                        ? 'Начало периода'
                        : 'Дата'
                    }
                    name={`${id}-start`}
                    value={start}
                    mode={mode === 'day'
                        ? 'date'
                        : 'datetime'
                    }
                    stepMinute={1}
                    maxDate={mode === 'range'
                        ? end ?? undefined
                        : undefined
                    }
                    onChange={(event) => {
                        setStart(event.value);
                        setError('');
                    }}
                />
                {mode === 'range' &&
                    <DateTimeInput
                        label="Конец периода"
                        name={`${id}-end`}
                        value={end}
                        mode="datetime"
                        stepMinute={1}
                        minDate={start ?? undefined}
                        onChange={(event) => {
                            setEnd(event.value);
                            setError('');
                        }}
                    />
                }
                <Button
                    type="button"
                    label={mode === 'day'
                        ? 'Сегодня'
                        : 'Сейчас'
                    }
                    text
                    onClick={() => setStart(getFilterNow())}
                />
                {validationError &&
                    <small role="alert">{validationError}</small>
                }
                <div className={classes.actions}>
                    <Button
                        type="button"
                        label="Сбросить"
                        text
                        onClick={() => {
                            onChange(null);
                            close();
                        }}
                    />
                    <Button
                        type="button"
                        label="Применить"
                        disabled={reversedRange}
                        onClick={apply}
                    />
                </div>
            </div>
        </OverlayPanel>
    </span>;
};
